/**
 * Single-source owner of workspace data + all mutation handlers.
 *
 * All raw `fetch` calls go through event-workspace-api.ts (the Go service via
 * the shared api-client).
 *
 * What lives here: data fetch, every `handleXyz` mutation, and the mutation
 * vocabulary (MUTATION_KEYS). Busy tracking, the error sink, and the
 * post-success refetch live in the MutationRunner (use-mutation-runner.ts,
 * #340 item 1) — handlers are one-liners through `mutations.run`, and
 * sections read button-loading state via `mutations.isPending(key)`. What
 * stays in the route component: accordion state, scroll coordination,
 * section refs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  awaitJobSummaryGeneration,
  awaitQuoteGeneration,
} from "./generated-artifacts";
import type { BookingPhase } from "./booking-phase";
import { useMutationRunner, type MutationRunner } from "./use-mutation-runner";
import {
  useSavableSections,
  type SavableSections,
} from "./use-savable-sections";
import {
  cancelEvent,
  confirmClientPayment,
  confirmContractorPayment,
  generateJobSummary,
  getEventDetails,
  patchWorkspace,
  sendBalanceInvoice,
  sendBalanceReminder,
  sendDepositReminder,
  type HeaderField,
  type WorkspaceEventData,
} from "./event-workspace-api";

export type { WorkspaceEventData };
export type { MutationRunner };
export type { SavableSections };

// The editable-header-column whitelist (HEADER_FIELDS / HeaderField) lives on
// the API module next to the EventInfoPatch wire type it constrains.
export type { HeaderField };

const HEADER_FIELD_KEY_PREFIX = "save-header-field:";

/**
 * The optimistic balance-invoice mask's single predicate: true once the
 * server's own data shows the balance invoice sent, at which point the mask
 * is redundant. Both applying the overlay and clearing the flag go through
 * this one function so the two conditions can never drift (sweep 2 D6).
 */
function serverShowsBalanceInvoiced(data: WorkspaceEventData): boolean {
  return Boolean(
    data.approval?.balance_invoiced_at ||
    data.paymentSummary.balanceStatus === "invoiced" ||
    data.paymentSummary.balanceStatus === "paid",
  );
}

/**
 * The workspace mutation vocabulary. The hook and the sections share these
 * keys so any component can ask `mutations.isPending(...)` for the mutation
 * it renders a button for — no per-mutation boolean props.
 */
export const MUTATION_KEYS = {
  sendDepositReminder: "send-deposit-reminder",
  sendBalanceReminder: "send-balance-reminder",
  sendBalanceInvoice: "send-balance-invoice",
  /** The save+POST half of quote generation (GearListSection). */
  generateQuoteSummary: "generate-quote-summary",
  /** The poll-for-new-PDF half of quote generation (handleQuoteGenerated). */
  generateQuote: "generate-quote",
  generateJobSummary: "generate-job-summary",
  cancelEvent: "cancel-event",
  saveHeaderNotes: "save-header-notes",
  saveStatusOverride: "save-status-override",
  sendQuote: "send-quote",
  sendQuoteReminder: "send-quote-reminder",
  notifyContractors: "notify-contractors",
  sendContractorReminder: "send-contractor-reminder",
  confirmClientPayment: (type: "deposit" | "balance") =>
    `confirm-client-payment:${type}`,
  confirmContractorPayment: (assignmentId: string) =>
    `confirm-contractor-payment:${assignmentId}`,
  /** Cancel one Assignment server-side (CrewSection's remove confirm). */
  removeAssignment: (assignmentId: string) =>
    `remove-assignment:${assignmentId}`,
  saveHeaderField: (field: HeaderField) => `${HEADER_FIELD_KEY_PREFIX}${field}`,
} as const;

export interface UseEventWorkspaceReturn {
  data: WorkspaceEventData | null;
  loading: boolean;
  error: string | null;
  fetchData: () => Promise<WorkspaceEventData | null>;

  /** Busy tracking + error sink for every workspace mutation. */
  mutations: MutationRunner;
  /**
   * Coarse "disable the other action buttons" signal. Excludes quote
   * generation and header autosaves, which run long (poll) or fire on blur
   * and shouldn't lock the rest of the workspace.
   */
  busy: boolean;
  /** Header fields with an in-flight save — derived view for EventHeader/EventInfoCards. */
  savingHeaderFields: Set<string>;

  localCrewCount: number | null;
  setLocalCrewCount: (v: number | null) => void;
  cancelConfirmOpen: boolean;
  setCancelConfirmOpen: (v: boolean) => void;

  /**
   * Sections with unsaved edits register here; generation flushes them all
   * via saveAll() without knowing which sections exist.
   */
  savables: SavableSections;

  saveHeaderField: (field: HeaderField, value: string | null) => Promise<void>;
  saveHeaderNotes: (value: string | null) => Promise<void>;
  saveStatusOverride: (phase: BookingPhase | null) => Promise<void>;
  handleSendBalanceInvoice: () => Promise<void>;
  handleSendDepositReminder: () => Promise<void>;
  handleSendBalanceReminder: () => Promise<void>;
  handleQuoteGenerated: (opts?: { onSuccess?: () => void }) => Promise<void>;
  handleGenerateJobSummary: (opts?: {
    onSuccess?: () => void;
  }) => Promise<void>;
  handleConfirmPayment: (
    assignmentId: string,
    paymentDate: string,
    hours: number,
  ) => Promise<void>;
  handleConfirmClientPayment: (
    type: "deposit" | "balance",
    date: string,
  ) => Promise<void>;
  handleCancelEvent: () => Promise<void>;
}

export function useEventWorkspace(id: string): UseEventWorkspaceReturn {
  const [data, setData] = useState<WorkspaceEventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [optimisticBalanceInvoicedAt, setOptimisticBalanceInvoicedAt] =
    useState<string | null>(null);

  const [localCrewCount, setLocalCrewCount] = useState<number | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const savables = useSavableSections();

  const fetchData =
    useCallback(async (): Promise<WorkspaceEventData | null> => {
      try {
        const result = await getEventDetails(id);
        setData(result);
        setError(null);
        return result;
      } catch (err) {
        console.error("Failed to load event:", err);
        setError(err instanceof Error ? err.message : String(err));
        return null;
      } finally {
        setLoading(false);
      }
    }, [id]);

  const mutations = useMutationRunner({ refetch: fetchData });

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Clear optimistic balance-invoice flag once the server data catches up.
  useEffect(() => {
    if (!optimisticBalanceInvoicedAt || !data) return;
    if (serverShowsBalanceInvoiced(data)) {
      setOptimisticBalanceInvoicedAt(null);
    }
  }, [data, optimisticBalanceInvoicedAt]);

  // Optimistic balance-invoice mask: while the refetch trails the send, patch
  // approval + paymentSummary so the payment section — and, via paymentSummary,
  // deriveWorkspaceView's balance state — read "invoiced" immediately.
  // Memoised so downstream useMemos keyed on data/approval/paymentSummary keep
  // stable identities across unrelated re-renders.
  const effectiveData = useMemo((): WorkspaceEventData | null => {
    if (!data || !optimisticBalanceInvoicedAt) return data;
    if (serverShowsBalanceInvoiced(data)) return data;
    return {
      ...data,
      approval: data.approval
        ? { ...data.approval, balance_invoiced_at: optimisticBalanceInvoicedAt }
        : data.approval,
      paymentSummary: { ...data.paymentSummary, balanceStatus: "invoiced" },
    };
  }, [data, optimisticBalanceInvoicedAt]);

  const busy = useMemo(() => {
    for (const key of mutations.pending) {
      if (key === MUTATION_KEYS.generateQuote) continue;
      if (key === MUTATION_KEYS.generateQuoteSummary) continue;
      if (key === MUTATION_KEYS.saveHeaderNotes) continue;
      if (key === MUTATION_KEYS.saveStatusOverride) continue;
      if (key.startsWith(HEADER_FIELD_KEY_PREFIX)) continue;
      return true;
    }
    return false;
  }, [mutations.pending]);

  const savingHeaderFields = useMemo(
    () =>
      new Set(
        [...mutations.pending]
          .filter((k) => k.startsWith(HEADER_FIELD_KEY_PREFIX))
          .map((k) => k.slice(HEADER_FIELD_KEY_PREFIX.length)),
      ),
    [mutations.pending],
  );

  const saveHeaderField = useCallback(
    async (field: HeaderField, value: string | null): Promise<void> => {
      if (!data) return;
      const current = (data.booking as Record<string, unknown>)[field];
      const normalisedCurrent =
        typeof current === "string"
          ? current
          : current == null
            ? ""
            : String(current);
      const normalisedNext = value ?? "";
      if (normalisedCurrent === normalisedNext) return;

      await mutations.run(
        MUTATION_KEYS.saveHeaderField(field),
        `Save ${field.replace(/_/g, " ")}`,
        () =>
          patchWorkspace(data.booking.id, "event_info", {
            [field]: normalisedNext,
          }),
      );
    },
    [data, mutations],
  );

  const saveHeaderNotes = useCallback(
    async (value: string | null): Promise<void> => {
      if (!data) return;
      const normalisedCurrent = data.booking.admin_header_notes ?? "";
      const normalisedNext = value ?? "";
      if (normalisedCurrent === normalisedNext) return;
      await mutations.run(
        MUTATION_KEYS.saveHeaderNotes,
        "Save header notes",
        () =>
          patchWorkspace(data.booking.id, "header_notes", {
            notes: normalisedNext,
          }),
      );
    },
    [data, mutations],
  );

  const saveStatusOverride = useCallback(
    async (phase: BookingPhase | null): Promise<void> => {
      if (!data) return;
      const normalisedCurrent =
        data.booking.status_override_phase &&
        data.booking.status_override_base_phase === data.booking.derived_phase
          ? data.booking.status_override_phase
          : null;
      if (normalisedCurrent === phase) return;
      await mutations.run(
        MUTATION_KEYS.saveStatusOverride,
        "Save display status",
        () => patchWorkspace(data.booking.id, "status_override", { phase }),
      );
    },
    [data, mutations],
  );

  const handleSendBalanceInvoice = useCallback(async (): Promise<void> => {
    if (!data) return;
    await mutations.run(
      MUTATION_KEYS.sendBalanceInvoice,
      "Send balance invoice",
      () => sendBalanceInvoice(data.booking.id),
      // Optimistic: show "invoiced" before the refetch lands; the effect
      // above clears it once the server confirms.
      {
        onSuccess: () =>
          setOptimisticBalanceInvoicedAt(new Date().toISOString()),
      },
    );
  }, [data, mutations]);

  const handleSendDepositReminder = useCallback(async (): Promise<void> => {
    if (!data) return;
    await mutations.run(
      MUTATION_KEYS.sendDepositReminder,
      "Send deposit reminder",
      () => sendDepositReminder(data.booking.id),
    );
  }, [data, mutations]);

  const handleSendBalanceReminder = useCallback(async (): Promise<void> => {
    if (!data) return;
    await mutations.run(
      MUTATION_KEYS.sendBalanceReminder,
      "Send balance reminder",
      () => sendBalanceReminder(data.booking.id),
    );
  }, [data, mutations]);

  // Generation completion is observed by polling, and the poll loop IS the
  // refetch — hence refetch: false. Which fields fingerprint each artifact
  // (and the timeouts) live in generated-artifacts.ts.
  const handleQuoteGenerated = useCallback(
    async ({ onSuccess }: { onSuccess?: () => void } = {}): Promise<void> => {
      const before = data;
      const ok = await mutations.run(
        MUTATION_KEYS.generateQuote,
        "Generate quote",
        () => awaitQuoteGeneration(before, fetchData),
        { refetch: false },
      );
      if (ok) onSuccess?.();
    },
    [data, fetchData, mutations],
  );

  const handleGenerateJobSummary = useCallback(
    async ({ onSuccess }: { onSuccess?: () => void } = {}): Promise<void> => {
      if (!data) return;
      const ok = await mutations.run(
        MUTATION_KEYS.generateJobSummary,
        "Generate job summary",
        async () => {
          // Flush every savable section's pending edits before snapshotting.
          try {
            await savables.saveAll();
          } catch (reason) {
            throw new Error(
              `could not save job details first (${reason instanceof Error ? reason.message : String(reason)})`,
            );
          }
          await generateJobSummary(data.booking.id);
          await awaitJobSummaryGeneration(data, fetchData);
        },
        { refetch: false },
      );
      if (ok) onSuccess?.();
    },
    [data, fetchData, mutations, savables],
  );

  const handleConfirmPayment = useCallback(
    async (
      assignmentId: string,
      paymentDate: string,
      hours: number,
    ): Promise<void> => {
      await mutations.run(
        MUTATION_KEYS.confirmContractorPayment(assignmentId),
        "Confirm payment",
        () => confirmContractorPayment(assignmentId, paymentDate, hours),
      );
    },
    [mutations],
  );

  const handleConfirmClientPayment = useCallback(
    async (type: "deposit" | "balance", date: string): Promise<void> => {
      if (!data) return;
      await mutations.run(
        MUTATION_KEYS.confirmClientPayment(type),
        `Confirm ${type} payment`,
        () => confirmClientPayment(data.booking.id, type, date),
      );
    },
    [data, mutations],
  );

  const handleCancelEvent = useCallback(async (): Promise<void> => {
    if (!data) return;
    await mutations.run(MUTATION_KEYS.cancelEvent, "Cancel event", () =>
      cancelEvent(data.booking.id),
    );
    setCancelConfirmOpen(false);
  }, [data, mutations]);

  return {
    data: effectiveData,
    loading,
    error,
    fetchData,

    mutations,
    busy,
    savingHeaderFields,

    localCrewCount,
    setLocalCrewCount,
    cancelConfirmOpen,
    setCancelConfirmOpen,

    savables,

    saveHeaderField,
    saveHeaderNotes,
    saveStatusOverride,
    handleSendBalanceInvoice,
    handleSendDepositReminder,
    handleSendBalanceReminder,
    handleQuoteGenerated,
    handleGenerateJobSummary,
    handleConfirmPayment,
    handleConfirmClientPayment,
    handleCancelEvent,
  };
}
